import { useCallback, useEffect, useState } from 'react';
import type { LeadRecord, LeadSearchResult, LeadStatus } from '@360t/shared';
import { api, ApiError, type Facets, type LeadFilters } from '../lib/api';
import { LeadDrawer } from '../components/LeadDrawer';
import { OpportunityBadge } from '../components/OpportunityBadge';
import { StatusSelect } from '../components/StatusSelect';

const EXAMPLES = [
  'dentists in Leeds',
  'manufacturing companies in Birmingham',
  'hotels in Chicago',
  'law firms in Manchester',
];

export function LeadsPage() {
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(30);
  const [autoAudit, setAutoAudit] = useState(true);
  const [source, setSource] = useState<'auto' | 'openstreetmap' | 'google_places'>('auto');
  const [websiteFilter, setWebsiteFilter] = useState<'any' | 'with' | 'without'>('any');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [lastSearch, setLastSearch] = useState<LeadSearchResult | null>(null);

  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [filters, setFilters] = useState<LeadFilters>({ sort: 'opportunity', take: 50 });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openLead, setOpenLead] = useState<string | null>(null);
  const [bulkNote, setBulkNote] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, f, pending] = await Promise.all([
        api.listLeads(filters),
        api.leadFacets(),
        api.unverifiedCount(),
      ]);
      setLeads(list.leads);
      setTotal(list.total);
      setFacets(f);
      setUnverified(pending.unverified);
    } catch (err) {
      setSearchError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const runSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    setSearchError(null);
    setSearching(true);
    setLastSearch(null);
    try {
      const result = await api.searchLeads({ query, limit, autoAudit, source, websiteFilter });
      setLastSearch(result);
      // Pin the table to the leads this search actually returned. Without this
      // the list below is the whole database, so a fresh search of Munich reads
      // as a handful of Munich rows buried in every lead ever found.
      const ids = result.leads.map((l) => l.id).slice(0, 200);
      setFilters((prev) => ({
        ...prev,
        ids: ids.length > 0 ? ids : undefined,
        skip: 0,
        take: Math.min(Math.max(ids.length, 50), 200),
      }));
    } catch (err) {
      setSearchError(
        err instanceof ApiError && err.details
          ? `${err.message}: ${JSON.stringify((err.details as any).formErrors ?? err.details)}`
          : (err as Error).message,
      );
    } finally {
      setSearching(false);
    }
  };

  const patch = async (id: string, data: Parameters<typeof api.updateLead>[1]) => {
    setBusyId(id);
    try {
      const updated = await api.updateLead(id, data);
      setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
    } finally {
      setBusyId(null);
    }
  };

  const scan = async (id: string, kind: 'audit' | 'qa') => {
    setBusyId(id);
    try {
      await (kind === 'audit' ? api.auditLead(id) : api.qaLead(id));
      // The job is queued; results arrive on the next refresh.
      setTimeout(() => void load(), 3000);
    } catch (err) {
      setSearchError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}" and all of its audits and QA runs? This cannot be undone.`)) return;
    setBusyId(id);
    try {
      await api.deleteLead(id);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setTotal((t) => t - 1);
    } finally {
      setBusyId(null);
    }
  };

  const setFilter = (patchFilters: Partial<LeadFilters>) =>
    setFilters((prev) => ({ ...prev, ...patchFilters, skip: 0 }));

  // The list is paged server-side. Without these controls the table simply
  // stopped at the first page and the rest of the pipeline was unreachable.
  const take = filters.take ?? 50;
  const skip = filters.skip ?? 0;
  const goToPage = (nextSkip: number) =>
    setFilters((prev) => ({ ...prev, skip: Math.max(0, nextSkip) }));

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      {/* ── search ── */}
      <form onSubmit={runSearch} className="card mb-6">
        <label className="label" htmlFor="lead-query">
          Find businesses
        </label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            id="lead-query"
            className="input"
            placeholder="dentists in Leeds"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            required
          />
          <button type="submit" className="btn-primary whitespace-nowrap" disabled={searching}>
            {searching ? 'Searching…' : 'Find leads'}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span>Try:</span>
          {EXAMPLES.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setQuery(e)}
              className="rounded-full border border-line px-2.5 py-1 hover:border-brand hover:text-brand-dark"
            >
              {e}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-6 text-sm text-slate-600">
          <label className="flex items-center gap-2">
            Max results
            <input
              type="number"
              min={1}
              max={200}
              className="w-20 rounded-md border border-line px-2 py-1 text-sm"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            />
          </label>
          <label className="flex items-center gap-2">
            Only include
            <select
              className="rounded-md border border-line px-2 py-1 text-sm"
              value={websiteFilter}
              onChange={(e) => setWebsiteFilter(e.target.value as typeof websiteFilter)}
            >
              <option value="any">Every business</option>
              <option value="without">Businesses with NO website</option>
              <option value="with">Businesses that HAVE a website</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            Source
            <select
              className="rounded-md border border-line px-2 py-1 text-sm"
              value={source}
              onChange={(e) => setSource(e.target.value as typeof source)}
            >
              <option value="auto">OpenStreetMap (free)</option>
              <option value="openstreetmap">OpenStreetMap only</option>
              <option value="google_places">Google Places (billed)</option>
            </select>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={autoAudit} onChange={(e) => setAutoAudit(e.target.checked)} />
            Auto-audit every lead that has a website
          </label>
        </div>

        {searchError && <p className="mt-4 rounded-lg bg-poor/10 px-3 py-2 text-sm text-poor">{searchError}</p>}

        {lastSearch && (
          <div className="mt-4 rounded-lg bg-canvas px-4 py-3 text-sm">
            <p className="text-ink">
              <strong>{lastSearch.found}</strong> businesses found for{' '}
              <strong>{lastSearch.searched.category}</strong> in <strong>{lastSearch.searched.city}</strong> —{' '}
              {lastSearch.withWebsite} list a website,{' '}
              <strong className="text-poor">{lastSearch.withoutWebsite} do not</strong>. {lastSearch.created} new,{' '}
              {lastSearch.duplicates} already known.
              {lastSearch.auditsQueued > 0 && ` ${lastSearch.auditsQueued} audits queued.`}
            </p>
            {lastSearch.searched.source === 'openstreetmap' && lastSearch.withoutWebsite > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                OpenStreetMap leaves the website tag off most businesses, so those{' '}
                {lastSearch.withoutWebsite} are unconfirmed. Run <strong>Verify websites</strong> below before
                treating them as prospects.
              </p>
            )}
            {lastSearch.warnings.map((w) => (
              <p key={w} className="mt-1 text-xs text-amber-700">
                {w}
              </p>
            ))}
          </div>
        )}
      </form>

      {/* ── filters ── */}
      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <input
          className="w-56 rounded-lg border border-line px-3 py-1.5 text-sm"
          placeholder="Search name, site, phone…"
          value={filters.search ?? ''}
          onChange={(e) => setFilter({ search: e.target.value || undefined })}
        />
        <select
          className="rounded-lg border border-line px-2 py-1.5"
          value={filters.status ?? ''}
          onChange={(e) => setFilter({ status: e.target.value || undefined })}
        >
          <option value="">All statuses</option>
          {(['new', 'contacted', 'replied', 'won', 'lost'] as LeadStatus[]).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="rounded-lg border border-line px-2 py-1.5"
          value={filters.city ?? ''}
          onChange={(e) => setFilter({ city: e.target.value || undefined })}
        >
          <option value="">All cities</option>
          {facets?.cities.map((c) => (
            <option key={c.value} value={c.value}>
              {c.value} ({c.count})
            </option>
          ))}
        </select>
        <select
          className="rounded-lg border border-line px-2 py-1.5"
          value={
            filters.hasWebsite === undefined
              ? ''
              : filters.hasWebsite
                ? 'true'
                : filters.websiteChecked === true
                  ? 'confirmed'
                  : filters.websiteChecked === false
                    ? 'unverified'
                    : 'false'
          }
          onChange={(e) => {
            const v = e.target.value;
            setFilter({
              hasWebsite: v === '' ? undefined : v === 'true',
              websiteChecked: v === 'confirmed' ? true : v === 'unverified' ? false : undefined,
            });
          }}
        >
          <option value="">Website: any</option>
          <option value="true">Has website</option>
          <option value="confirmed">No website — checked</option>
          <option value="unverified">No website listed — unchecked</option>
          <option value="false">No website (either)</option>
        </select>
        <select
          className="rounded-lg border border-line px-2 py-1.5"
          value={filters.hasEmail === undefined ? '' : String(filters.hasEmail)}
          onChange={(e) =>
            setFilter({ hasEmail: e.target.value === '' ? undefined : e.target.value === 'true' })
          }
        >
          <option value="">Email: any</option>
          <option value="true">Has email</option>
          <option value="false">No email yet</option>
        </select>
        <select
          className="rounded-lg border border-line px-2 py-1.5"
          value={filters.platform ?? ''}
          onChange={(e) => setFilter({ platform: e.target.value || undefined })}
        >
          <option value="">All platforms</option>
          {facets?.platforms?.map((p) => (
            <option key={p.value ?? ''} value={p.value ?? ''}>
              {p.value} ({p.count})
            </option>
          ))}
        </select>
        <button
          type="button"
          className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold hover:border-brand hover:text-brand-dark disabled:opacity-40"
          disabled={busyId === 'bulk'}
          onClick={async () => {
            setBusyId('bulk');
            setBulkNote(null);
            try {
              const r = await api.enrichMissing(10);
              setBulkNote(
                `Enriched ${r.enriched} of ${r.attempted} leads. ${r.phoneOnlyMarked} phone-only leads marked contactable.`,
              );
              await load();
            } catch (err) {
              setBulkNote((err as Error).message);
            } finally {
              setBusyId(null);
            }
          }}
        >
          {busyId === 'bulk' ? 'Finding contacts…' : 'Find contacts (10)'}
        </button>
        <button
          type="button"
          title="OpenStreetMap leaves the website tag off most businesses, so an unchecked lead may well have a site. This looks for it."
          className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold hover:border-brand hover:text-brand-dark disabled:opacity-40"
          disabled={busyId === 'verify' || unverified === 0}
          onClick={async () => {
            setBusyId('verify');
            setBulkNote(null);
            try {
              const r = await api.verifyWebsites(10);
              setBulkNote(
                `Checked ${r.attempted} leads: found a website for ${r.websitesFound}, confirmed ${r.confirmedWithout} have none. ${r.remaining} still unchecked.`,
              );
              await load();
            } catch (err) {
              setBulkNote((err as Error).message);
            } finally {
              setBusyId(null);
            }
          }}
        >
          {busyId === 'verify' ? 'Checking websites…' : `Verify websites (${Math.min(unverified, 10)})`}
        </button>
        <select
          className="rounded-lg border border-line px-2 py-1.5"
          value={filters.sort}
          onChange={(e) => setFilter({ sort: e.target.value as LeadFilters['sort'] })}
        >
          <option value="opportunity">Sort: opportunity</option>
          <option value="created">Sort: newest</option>
          <option value="name">Sort: name</option>
        </select>
        {filters.ids && (
          <button
            type="button"
            className="rounded-lg border border-brand px-3 py-1.5 text-xs font-semibold text-brand-dark hover:bg-brand/5"
            onClick={() => setFilter({ ids: undefined, take: 50 })}
          >
            Showing this search only — show all leads
          </button>
        )}
        <span className="ml-auto text-xs text-slate-400">
          {loading
            ? 'Loading…'
            : total === 0
              ? 'no leads'
              : `${skip + 1}–${skip + leads.length} of ${total} lead${total === 1 ? '' : 's'}`}
        </span>
      </div>

      {bulkNote && <p className="mb-3 rounded-lg bg-canvas px-3 py-2 text-sm text-ink">{bulkNote}</p>}

      {/* ── table ── */}
      <div className="card overflow-x-auto p-0">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-400">
              <th className="px-4 py-3">Score</th>
              <th className="px-4 py-3">Business</th>
              <th className="px-4 py-3">Website</th>
              <th className="px-4 py-3">Platform</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Audit / QA</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-400">
                  No leads yet. Run a search above.
                </td>
              </tr>
            )}
            {leads.map((lead) => (
              <tr key={lead.id} className={`border-b border-line last:border-0 ${lead.doNotContact ? 'opacity-45' : ''}`}>
                <td className="px-4 py-3">
                  <OpportunityBadge score={lead.opportunityScore} />
                </td>
                <td className="px-4 py-3">
                  <button
                    className="text-left font-semibold text-ink hover:text-brand-dark hover:underline"
                    onClick={() => setOpenLead(lead.id)}
                  >
                    {lead.businessName}
                  </button>
                  <div className="text-xs text-slate-400">
                    {[lead.city, lead.category?.split('=')[1] ?? lead.category].filter(Boolean).join(' · ')}
                    {lead.phone && ` · ${lead.phone}`}
                  </div>
                  {lead.chainBrand && (
                    <span className="mt-0.5 inline-block rounded bg-canvas px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">
                      {lead.chainBrand} chain branch
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {lead.websiteUrl ? (
                    <a
                      href={lead.websiteUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand-dark hover:underline"
                    >
                      {lead.websiteUrl.replace(/^https?:\/\/(www\.)?/, '').slice(0, 30)}
                    </a>
                  ) : lead.websiteCheckedAt ? (
                    <span className="rounded bg-poor/10 px-2 py-0.5 text-xs font-bold text-poor">NO WEBSITE</span>
                  ) : (
                    <span
                      title="The source did not list a website. Nobody has checked whether one exists — run Verify websites."
                      className="rounded bg-canvas px-2 py-0.5 text-xs font-bold text-slate-500"
                    >
                      NONE LISTED · UNCHECKED
                    </span>
                  )}
                  {lead.websiteBroken && (
                    <div className="mt-0.5 text-[10px] font-bold text-poor">SITE DOES NOT LOAD</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  {lead.platform ? (
                    <span className="rounded bg-canvas px-2 py-1 font-semibold text-ink">{lead.platform}</span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                  {lead.revampSignals.length > 0 && (
                    <div className="mt-0.5 text-[10px] text-poor">{lead.revampSignals.length} revamp signal(s)</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">
                  {lead.email ? (
                    <a href={`mailto:${lead.email}`} className="text-brand-dark hover:underline">
                      {lead.email.length > 24 ? `${lead.email.slice(0, 22)}…` : lead.email}
                    </a>
                  ) : (
                    <span className="text-slate-300">no email</span>
                  )}
                  <div className="text-[11px] text-slate-400">
                    {lead.phone ?? ''}
                    {lead.socialLinks && Object.keys(lead.socialLinks).length > 0 && (
                      <span className="ml-1">· {Object.keys(lead.socialLinks).length} social</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div>
                    {lead.latestAudit ? (
                      lead.latestAudit.status === 'completed' ? (
                        <span className="text-ink">Audit {lead.latestAudit.overallScore}/100</span>
                      ) : (
                        <span className="text-slate-400">Audit {lead.latestAudit.status}</span>
                      )
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </div>
                  <div>
                    {lead.latestQaRun ? (
                      lead.latestQaRun.status === 'completed' ? (
                        <span className="text-ink">QA {lead.latestQaRun.qaScore}/100</span>
                      ) : (
                        <span className="text-slate-400">QA {lead.latestQaRun.status}</span>
                      )
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <StatusSelect
                    value={lead.status}
                    disabled={busyId === lead.id}
                    onChange={(status) => void patch(lead.id, { status })}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {lead.websiteUrl && (
                      <>
                        <button
                          className="rounded border border-line px-2 py-1 text-xs hover:border-brand hover:text-brand-dark disabled:opacity-40"
                          disabled={busyId === lead.id}
                          onClick={() => void scan(lead.id, 'audit')}
                        >
                          Audit
                        </button>
                        <button
                          className="rounded border border-line px-2 py-1 text-xs hover:border-brand hover:text-brand-dark disabled:opacity-40"
                          disabled={busyId === lead.id}
                          onClick={() => void scan(lead.id, 'qa')}
                        >
                          QA
                        </button>
                      </>
                    )}
                    {lead.latestAudit?.pdfPath && (
                      <a
                        href={lead.latestAudit.pdfPath}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded border border-line px-2 py-1 text-xs hover:border-brand hover:text-brand-dark"
                      >
                        PDF
                      </a>
                    )}
                    <button
                      title="Do not contact"
                      className={`rounded border px-2 py-1 text-xs ${
                        lead.doNotContact ? 'border-poor text-poor' : 'border-line text-slate-400 hover:text-ink'
                      }`}
                      disabled={busyId === lead.id}
                      onClick={() => void patch(lead.id, { doNotContact: !lead.doNotContact })}
                    >
                      DNC
                    </button>
                    <button
                      className="rounded border border-line px-2 py-1 text-xs text-slate-400 hover:border-poor hover:text-poor"
                      disabled={busyId === lead.id}
                      onClick={() => void remove(lead.id, lead.businessName)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > take && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            type="button"
            className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold hover:border-brand hover:text-brand-dark disabled:opacity-40"
            disabled={skip === 0 || loading}
            onClick={() => goToPage(skip - take)}
          >
            ← Previous
          </button>
          <span className="text-xs text-slate-400">
            Page {Math.floor(skip / take) + 1} of {Math.max(1, Math.ceil(total / take))}
          </span>
          <button
            type="button"
            className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold hover:border-brand hover:text-brand-dark disabled:opacity-40"
            disabled={skip + take >= total || loading}
            onClick={() => goToPage(skip + take)}
          >
            Next →
          </button>
        </div>
      )}

      {openLead && (
        <LeadDrawer leadId={openLead} onClose={() => setOpenLead(null)} onChanged={() => void load()} />
      )}
    </div>
  );
}
